// ISDP CUSTOM: Shared confirm-dialog context + hook.
//
// Same pattern as ./toast: the context lives in isdp-common so BOTH the app AND
// app-facing plugins (e.g. the admin plugin, which can't import from packages/app)
// resolve the SAME React context instance and share the single ConfirmProvider the
// app mounts at its root. The dialog's rendering/CSS/i18n stays in packages/app.
import { createContext, useContext, type ReactNode } from 'react';

export interface ConfirmOptions {
  title: string;
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Style the confirm button as destructive (red). Default true — most uses are deletes. */
  danger?: boolean;
}

export type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

export const ConfirmContext = createContext<ConfirmFn | null>(null);

/** Access the confirm() function. Falls back to window.confirm outside the provider. */
export const useConfirm = (): ConfirmFn => {
  const ctx = useContext(ConfirmContext);
  return (
    ctx ??
    (async (options: ConfirmOptions) =>
      typeof window !== 'undefined'
        ? // eslint-disable-next-line no-alert -- safe native fallback if used outside the provider (never hit in practice)
          window.confirm(options.title)
        : true)
  );
};
